import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation before fully releasing sticky
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Duration (ms) matching `--header-sticky-duration` for show/hide animation
   * @type {number}
   */
  #stickyAnimDuration = 350;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * RAF ID for sticky show animation (idle → active). Must be cancelled on reset
   * so a stale frame cannot re-activate the header after returning to page top.
   * @type {number | null}
   */
  #stickyShowRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Scroll distance (px) before the sticky header may activate
   * @returns {number}
   */
  get #stickyThreshold() {
    const value = Number(this.dataset.stickyThreshold);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * Observes the header while scrolling the viewport to track when its offscreen
   * (used for scroll-up sticky mode)
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      // Treat idle (slide show/hide) like active — otherwise the translated
      // header can look "intersecting", clear #offscreen, and the scroll
      // handler early-returns without ever releasing sticky at page top.
      const stickyMounted =
        this.dataset.stickyState === 'active' || this.dataset.stickyState === 'idle';
      this.#offscreen = !entry.isIntersecting || stickyMounted;
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root (scroll-up only)
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    if (stickyMode === 'scroll-up') {
      this.#observeStickyPosition();
    }

    this.#updateScrollState();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #clearStickyTimeout() {
    if (this.#timeout !== null) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
  }

  #clearStickyShowRaf() {
    if (this.#stickyShowRafId !== null) {
      cancelAnimationFrame(this.#stickyShowRafId);
      this.#stickyShowRafId = null;
    }
  }

  /**
   * Sets sticky state with slide show/hide animation.
   * @param {'active' | 'idle' | 'inactive'} state
   * @param {{ animate?: boolean }} [options]
   */
  #setStickyState(state, { animate = true } = {}) {
    const current = this.dataset.stickyState || 'inactive';
    if (current === state) return;

    this.#clearStickyTimeout();

    // Instant reset when returning to natural document position
    if (state === 'inactive' && !animate) {
      this.#clearStickyShowRaf();
      this.dataset.stickyState = 'inactive';
      return;
    }

    // Hide: slide up, then release sticky if fully inactive
    if ((state === 'idle' || state === 'inactive') && current === 'active') {
      this.#clearStickyShowRaf();
      this.dataset.stickyState = 'idle';

      if (state === 'inactive') {
        this.#timeout = window.setTimeout(() => {
          this.#timeout = null;
          if (this.dataset.stickyState === 'idle') {
            this.dataset.stickyState = 'inactive';
          }
        }, this.#stickyAnimDuration);
      }
      return;
    }

    // Already hidden — release sticky without animating again
    if (state === 'inactive' && current === 'idle') {
      this.#clearStickyShowRaf();
      this.dataset.stickyState = 'inactive';
      return;
    }

    if (state === 'idle') {
      this.#clearStickyShowRaf();
      this.dataset.stickyState = 'idle';
      return;
    }

    // Show: mount hidden (idle), then slide down into active.
    // Skip for sticky=always — going through idle would flash the header off then on.
    if (state === 'active' && current === 'inactive' && animate) {
      if (this.getAttribute('sticky') === 'always') {
        this.dataset.stickyState = 'active';
        return;
      }

      this.#clearStickyShowRaf();
      this.dataset.stickyState = 'idle';
      // Force reflow so the hidden state paints before the enter transition
      void this.offsetHeight;
      this.#stickyShowRafId = requestAnimationFrame(() => {
        this.#stickyShowRafId = null;
        // Abort if scroll already released sticky (e.g. fast scroll back to top)
        if (this.dataset.stickyState !== 'idle') return;
        this.dataset.stickyState = 'active';
      });
      return;
    }

    this.dataset.stickyState = state;
  }

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const scrollDelta = scrollTop - this.#lastScrollTop;
    // Ignore trackpad/touch micro-jitter so sticky doesn't thrash active ↔ idle
    const isScrollingUp = scrollDelta < -2;
    const isScrollingDown = scrollDelta > 2;
    const isAtTop = headerTop >= 0;
    const pastThreshold = scrollTop >= this.#stickyThreshold;

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.#setStickyState('inactive', { animate: false });
        this.dataset.scrollDirection = 'none';
      } else if (!pastThreshold) {
        this.#setStickyState('inactive', { animate: false });
        this.dataset.scrollDirection = 'none';
      } else {
        this.#setStickyState('active');
        this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // scroll-up: always release when back under the threshold / at natural top.
    // Do this before the #offscreen early-return — otherwise a stale idle frame
    // can leave sticky active and PDP columns offset after scrolling home.
    // Only clear #offscreen at the real top; while scroll is still mid-page but
    // under the threshold the header may already be off-screen and we must
    // keep that so sticky can activate again on the next scroll-up.
    if (isAtTop) {
      this.#offscreen = false;
      this.#setStickyState('inactive', { animate: false });
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!pastThreshold) {
      this.#setStickyState('inactive', { animate: false });
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = scrollTop;
      return;
    }

    // Wait until the header has left the viewport (or is already sticky)
    if (!this.#offscreen) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      // show sticky header when scrolling up past the threshold
      this.#setStickyState('active');
      this.dataset.scrollDirection = 'up';
    } else if (isScrollingDown && this.dataset.stickyState === 'active') {
      // Hide only while clearly scrolling down from a visible sticky header
      this.dataset.scrollDirection = 'none';
      this.#setStickyState('idle');
    }
    // When past threshold but never shown (inactive) or already hidden (idle),
    // stay put — do not flip inactive → idle on scroll down (that sticks the
    // header for a frame then hides it = blink).

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      if (stickyMode === 'scroll-up') {
        this.#observeStickyPosition();
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
        this.#updateScrollState();
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    this.#clearStickyTimeout();
    this.#clearStickyShowRaf();
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
